import {
    Checkbox as PrimeCheckbox,
    CheckboxProps as PrimeCheckboxProps,
} from 'primereact/checkbox';
import { forwardRef, ReactNode, Ref, useId } from 'react';
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    useFormContext,
} from 'react-hook-form';
import { InputHintError } from '../../_utils/components';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';

export interface CheckboxProps extends Omit<PrimeCheckboxProps, 'checked'> {
    name?: string;
    label?: string | ReactNode;
    description?: string | ReactNode;
    children?: ReactNode;
    checked?: boolean;
    /**
     * Custom react-hook-form validation function
     */
    validate?: FieldValidate;
    'data-testid'?: string;
}

function CheckboxComponent(
    props: CheckboxProps,
    ref: Ref<PrimeCheckbox>,
): ReactNode {
    const {
        name,
        label,
        description,
        children,
        checked,
        onChange,
        onBlur,
        validate,
        required,
        'data-testid': dataTestId,
        ...rest
    } = props;
    const testId = dataTestId || name;
    const formContext = useFormContext();
    const isControlled = typeof checked === 'boolean' ? true : false;
    const generatedInputId = useId();
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'Checkbox',
        isRHFControlled,
        onChange,
        ref,
    });

    const checkboxContent = (
        field?: ControllerRenderProps<FieldValues, string>,
        fieldState?: ControllerFieldState,
    ) => {
        const inputId = rest.inputId || name || `checkbox-${generatedInputId}`;

        return (
            <div className="flex flex-column gap-2">
                <div
                    className={`flex gap-2 ${
                        children ? 'align-items-start' : 'align-items-center'
                    }`}
                >
                    <PrimeCheckbox
                        ref={field ? mergeRefs(ref, field.ref) : ref}
                        pt={{ input: { 'data-testid': testId } }}
                        name={name}
                        inputId={inputId}
                        checked={
                            field ? Boolean(field?.value) : Boolean(checked)
                        }
                        value={field ? Boolean(field?.value) : Boolean(checked)}
                        onChange={(e) => {
                            if (field) {
                                field?.onChange(e);
                                return;
                            }

                            if (onChange) {
                                onChange(e);
                            }
                        }}
                        onBlur={(e) => {
                            field?.onBlur();
                            onBlur?.(e);
                        }}
                        invalid={fieldState?.invalid}
                        required={required}
                        {...rest}
                    />
                    <label
                        htmlFor={inputId}
                        className={rest.disabled ? undefined : 'cursor-pointer'}
                    >
                        {!children ? (
                            <span>
                                {label}{' '}
                                {description ? (
                                    <>
                                        <br /> {description}
                                    </>
                                ) : null}
                            </span>
                        ) : null}
                        {children ? <div>{children}</div> : null}
                    </label>
                </div>
                <InputHintError fieldState={fieldState} />
            </div>
        );
    };

    if (isControlled || !formContext || !name) {
        return checkboxContent();
    }

    return (
        <Controller
            name={name}
            control={formContext.control}
            rules={buildRhfRules({ required, validate })}
            render={({ field, fieldState }) =>
                checkboxContent(field, fieldState)
            }
        />
    );
}

export const Checkbox = forwardRef(CheckboxComponent);
